import { useContext, useEffect, useState } from "react";
import { useWindowSize } from "usehooks-ts";

import { useNotificationsPanelInfoGetV2 } from "@btnet/services-btnet/handlers/notifications/notifications";
import { cn } from "@btnet/utilities-core";

import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { queryClient } from "helpers/queryClient";

import { BTLocalStorage } from "types/btStorage";
import { MenuItemName } from "types/enum";

import { useTracking } from "utilities/analytics/analytics";
import { routes } from "utilities/routes";

import { BTBadge } from "commonComponents/btWrappers/BTBadge/BTBadge";
import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import {
    BTIconCloseOutlined,
    BTIconHelp,
    BTIconLiveChat,
    BTIconNotifications,
    BTIconSearch,
    BTIconUsers,
} from "commonComponents/btWrappers/BTIcon";
import { BTLink } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTUser } from "commonComponents/btWrappers/BTUser/BTUser";
import { MainNavDropdown } from "commonComponents/utilities/MainNavigation/common/MainNavDropdown";
import MainNavTooltip from "commonComponents/utilities/MainNavigation/common/MainNavTooltip";
import { GlobalSearch } from "commonComponents/utilities/MainNavigation/globalSearch/GlobalSearch";
import { type IMainNavActionItemsProps } from "commonComponents/utilities/MainNavigation/MainNavActionItems/MainNavActionItems.types";
import {
    ChatReadQueryKey,
    useGetChatQuery,
} from "commonComponents/utilities/MainNavigation/MainNavigation.api.handler";
import { menuItemClass } from "commonComponents/utilities/MainNavigation/MainNavigation.types";
import { ExperimentPersonalizedQuickAdd } from "commonComponents/utilities/MainNavigation/MainNavQuickAddExperiment";

import { NotificationPanel } from "entity/notification/panel/NotificationPanel";

/** renders global search through settings */
export const MainNavActionItems: React.FC<
    IMainNavActionItemsProps & React.HTMLAttributes<HTMLDivElement>
> = ({
    className,
    userId,
    selectedJobId,
    navData,
    isTemplateMode,
    selectedMenuItem,
    subMenuProps,
    handler,
    userInfoContext,
    handleShowSearchInput,
    handleHideSearchInput,
    ...props
}) => {
    const userInfo = useRequiredContext(UserInfoContext);
    const appDefaultInfo = useContext(AppDefaultInfoContext);
    const { width = 0 } = useWindowSize();
    const { headerStyleInfo, isSearchEnabled } = navData;
    const allowSearch = isSearchEnabled && !isTemplateMode;
    const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
    const { trackEvent } = useTracking();

    // This fixes the issue where the suffix on BTOverflow does not correctly have a relative position
    // when the window is resized to be smaller than 1250px
    useEffect(() => {
        const element = document.querySelector(".rc-overflow-item-suffix");
        if (width < 1250) {
            element?.classList.add("force-relative");
        } else {
            element?.classList.remove("force-relative");
        }
    }, [width]);

    const trackNotificationPanelClose = () => {
        trackEvent({
            // Event added in ado-244064
            // Until then, we will cast to keep TypeScript happy
            event: "PopoverClose" as "PopoverOpen",
            uniqueId: "notificationPanelClose",
        });
    };

    const getNotificationMenuItem = () => {
        return (
            <div className={menuItemClass}>
                <NotificationPanel
                    selectedJobId={selectedJobId}
                    onClose={() => {
                        trackNotificationPanelClose();
                        setNotificationPanelOpen(false);
                    }}
                />
            </div>
        );
    };

    const isReadQueryEnabled =
        !userInfo.isBuilderImpersonatingOwner &&
        (navData?.isChatEnabled ?? false) === true &&
        selectedMenuItem !== MenuItemName.Chat;

    const {
        data: hasUnreadChatData,
        isFetching: isFetchingChatReadData,
        refetch: refetchChatReadData,
    } = useGetChatQuery(handler, appDefaultInfo?.chatMainNavIndicatorPollingRateInSeconds ?? 60, {
        enabled: isReadQueryEnabled,
    });

    const hasUnreadChat = isReadQueryEnabled && hasUnreadChatData?.hasUnread;

    useEffect(() => {
        (async () => {
            const hasUnreadData = BTLocalStorage.get("bt-boolean-hasUnreadChat");
            if (!isFetchingChatReadData && hasUnreadData === null) {
                await queryClient.invalidateQueries([ChatReadQueryKey]);
            }
        })();
    }, [isFetchingChatReadData, refetchChatReadData]);
    return (
        <div className={cn("MainNavActionItems", className)} {...props}>
            <div
                key="globalSearch"
                className={cn("menu-item-no-hover", "MenuItem-SearchBar", "GlobalSearch-SearchBar")}
            >
                <div
                    className={cn("MenuItem-SearchBar--search-wrap")}
                    style={{
                        backgroundColor: headerStyleInfo.color,
                    }}
                >
                    {allowSearch && (
                        <GlobalSearch
                            searchRequestedPage={selectedMenuItem}
                            builderIdUserIdLookup={navData.builderIdUserIdLookup}
                            handleHideSearchInput={handleHideSearchInput}
                        />
                    )}
                    <BTButtonDeprecated
                        className="MainNavDropdown MainNavDropdown-icon-only MainNavDropdown-search-close"
                        data-testid="search-close-main-nav-button"
                        aria-label="Search"
                        onClick={handleHideSearchInput}
                        noShadow
                    >
                        <BTIconCloseOutlined className="MainNavDropdown-search-close--icon" />
                    </BTButtonDeprecated>
                </div>
                {allowSearch && (
                    <BTButtonDeprecated
                        className="MainNavDropdown MainNavDropdown-icon-only MainNavDropdown-search-action"
                        data-testid="search-main-nav-button"
                        aria-label="Search"
                        onClick={handleShowSearchInput}
                        noShadow
                    >
                        <BTIconSearch className="MainNavDropdown-search-action--icon" />
                    </BTButtonDeprecated>
                )}
            </div>
            <ExperimentPersonalizedQuickAdd {...subMenuProps} />

            <MainNavDropdown
                {...subMenuProps}
                intercomTarget="Notification Icon"
                itemKey="notifications"
                data-testid="notifications-main-nav-button"
                aria-label="Notifications Menu"
                destroyPopupOnHide={true}
                customMenuContent={getNotificationMenuItem()}
                className="MainNavDropdown-icon-only"
                tooltip="Notifications"
                visible={notificationPanelOpen}
                onVisibleChange={(visible) => {
                    setNotificationPanelOpen(visible);
                    if (!visible) {
                        trackNotificationPanelClose();
                    }
                }}
            >
                <NewNotificationsBadge
                    refetchInternalSeconds={
                        appDefaultInfo?.unreadNotificationCountCacheTimeout ?? 300
                    }
                >
                    <BTIconNotifications />
                </NewNotificationsBadge>
            </MainNavDropdown>

            {navData.isChatEnabled && (
                <BTLink useAutoSPARouting to={routes.chat.getListLink()}>
                    <MainNavTooltip title="Chat" className="MainNavDropdown" enabled>
                        <BTButtonDeprecated
                            className="MainNavDropdown MainNavDropdown-icon-only"
                            data-testid="chat-main-nav-button"
                            aria-label="Chat Button"
                            noShadow
                        >
                            <MainNavBadge dot={hasUnreadChat}>
                                <BTIconLiveChat />
                            </MainNavBadge>
                        </BTButtonDeprecated>
                    </MainNavTooltip>
                </BTLink>
            )}

            <MainNavDropdown
                {...subMenuProps}
                itemKey="users"
                data-testid="users-main-nav-button"
                aria-label="User management"
                className="MainNavDropdown-icon-only"
                tooltip="User management"
            >
                <BTIconUsers isOnDark />
            </MainNavDropdown>

            <MainNavDropdown
                {...subMenuProps}
                itemKey="help"
                data-testid="help-main-nav-button"
                aria-label="Help Menu"
                className="MainNavDropdown-icon-only"
                tooltip="Help"
            >
                <BTIconHelp />
            </MainNavDropdown>

            <MainNavDropdown
                {...subMenuProps}
                itemKey="setup"
                data-testid="settings-main-nav-button"
                aria-label="Settings Menu"
                className="MainNavDropdown-icon-only"
                tooltip="Settings"
                tooltipPlacement="bottomLeft"
            >
                <BTUser
                    size="small"
                    displayName={userInfoContext?.fullName || ""}
                    globalUserId={userInfoContext?.globalUserId || -1}
                    contactCard="none"
                />
            </MainNavDropdown>
        </div>
    );
};

interface IMainNavBadgeProps {
    count?: number;
    dot?: boolean;
    children: React.ReactNode;
}

export const MainNavBadge: React.FunctionComponent<IMainNavBadgeProps> = (props) => {
    return (
        <BTBadge count={props.count} dot={props.dot} offset={[-5, 0]}>
            {props.children}
        </BTBadge>
    );
};

interface INewNotificationsBadgeProps {
    children: React.ReactNode;
    refetchInternalSeconds: number;
}

export const NewNotificationsBadge: React.FunctionComponent<INewNotificationsBadgeProps> = (
    props,
) => {
    const { data: panelInfoData } = useNotificationsPanelInfoGetV2({
        query: { refetchInterval: 1000 * props.refetchInternalSeconds },
    });

    return (
        <BTBadge dot={panelInfoData?.data.hasNotificationsSinceLastOpened} offset={[-5, 0]}>
            {props.children}
        </BTBadge>
    );
};
